import { ROOM_GAMES } from '@gardener/content';
import { ByteReader, ByteWriter, ProtocolError } from './bytes';
import { array, type Codec, type Infer, optional, type StructCodec, struct, t, taggedUnion } from './codec';

/**
 * Bump on ANY change to a wire shape, message order, or quantization, and on any code change to a predicted system
 * (hard rule 4). Clients with a different version, or a different balance (`hello.content`), are rejected at
 * hello (ADR-0062). See .claude/skills/netcode.
 */
export const PROTOCOL_VERSION = 148;

/** 1/16 px positions. */
const pos = t.fixed(16);

// ─── Client → server ────────────────────────────────────────────────────────────────────────────────

/**
 * `reach`: how far the crosshair is from the gardener along `aim`, px (the Magic Wand's storm chases it, ADR-0097).
 * `view`: the server tick the client was showing when it sampled the input (`NetClient.renderTick`, whole ticks; 0
 * before its clock is in sync). The match ignores it; a replay's tape keeps it (ADR-0126): what the player saw when
 * they pressed.
 */
export const WireInput = struct({
  seq: t.varuint,
  buttons: t.varuint,
  aim: t.angle16,
  reach: t.varuint,
  view: t.varuint,
});
export type WireInput = Infer<typeof WireInput>;

export const ClientMessage = taggedUnion('type', {
  /**
   * The handshake (ADR-0062). Pinned for every version to come (protocol.test.ts): variant 0, `version` first, so a
   * server reads any client's version before the rest; `content` is the client's balance (content `contentHash()`).
   */
  hello: struct({ version: t.u16, name: t.string, content: t.string }),
  /**
   * Newest last. May repeat recent inputs for redundancy; the server drops seqs it already has. `seen`: the newest
   * snapshot (its tick) the client holds whole, which the next ones may be written against (ADR-0204); 0: none, send
   * them whole.
   */
  inputs: struct({ inputs: array(WireInput), seen: t.varuint }),
  ping: struct({ time: t.f64 }),
  /** Ready (or not) for the next round / to leave the lobby (ADR-0015). Answered with a `roster`. */
  ready: struct({ ready: t.bool }),
  /**
   * Call the held strike on world point (x, y) px (ADR-0019); an Alien Attack's laser runs from there to (x2, y2)
   * (ADR-0099; the same point: across it, away from its caller). Answered with a `strike` event.
   */
  strike: struct({ x: t.u16, y: t.u16, x2: t.u16, y2: t.u16 }),
  /**
   * A custom room's hub order (ADR-0045 amended, ADR-0063), obeyed in the lobby phase of a private room only: its owner
   * (the host) may `kick` a player out of the room. Answered with a `roster`.
   */
  hub: struct({
    action: taggedUnion('kind', {
      kick: struct({ id: t.varuint }),
    }),
  }),
  /** Ping world point (x, y) px for your team (a right click on the minimap, ADR-0069). Answered with a `signal` event. */
  signal: struct({ x: t.u16, y: t.u16 }),
  /**
   * Take gun card `id` (a NetId, ADR-0068: right click on it). Settled by the next tick when it is still there and in
   * reach; answered with a `taken` event.
   */
  take: struct({ id: t.varuint }),
  /** The shooting range (ADR-0100): hand me gun `weapon` (content id) from the armoury. Settled by the next tick. */
  arm: struct({ weapon: t.u8 }),
  /**
   * A Zombie Nights order (ADR-0098): while a run is played, day or night (ADR-0183), buy a repair (`item`: content
   * ZOMBIE_SHOP id), place a build (`item` — a fence or a tower — on cell (cx, cy); a build never moves), sell build
   * `id`, upgrade it on path `path` (0: A, 1: B; ADR-0171), or make it a Paragon (or feed the one it is: ADR-0181); take
   * card `card` (0-2) of the ones you are offered. Answered with an `order` event.
   */
  zombie: struct({
    action: taggedUnion('kind', {
      buy: struct({ item: t.u8 }),
      place: struct({ item: t.u8, cx: t.u8, cy: t.u8 }),
      sell: struct({ id: t.varuint }),
      pick: struct({ card: t.u8 }),
      upgrade: struct({ id: t.varuint, path: t.u8 }),
      paragon: struct({ id: t.varuint }),
    }),
  }),
  /**
   * What the client saw, for a replay (ADR-0126): a JSON line (`DiagNote`, within `DIAG`) — the device once, then how
   * the game ran every few seconds and the errors it hit. The match ignores it; the tape keeps it at the tick it arrived.
   */
  diag: struct({ text: t.string }),
  /**
   * A line of the room's chat (ADR-0170): everyone seated in the room hears it (`chat`), or only the speaker's `team`, or
   * one player whispered `to` (their NetId; 0: no whisper), rude words starred, nobody either of the two blocked. Within
   * `ROOM_CHAT` (`chatLine` cleans it as the client sends it); answered by the line coming back as they hear it.
   */
  chat: struct({ text: t.string, team: t.bool, to: t.varuint }),
  /**
   * Zombie Nights at double speed, or not (ADR-0173): the run plays at 2× while every human here wants it (the roster's
   * `fast`). Answered with a `roster`.
   */
  speed: struct({ fast: t.bool }),
  /**
   * Zombie Nights' days start their nights by themselves, or not (ADR-0183, OPTIONS' Auto-start nights): while anyone
   * here says no, a day waits for everyone's READY (`MatchInfo.zombies.waits`). A session says yes until it says no.
   */
  autostart: struct({ on: t.bool }),
  /**
   * The UDP lane's offer (ADR-0178): a WebRTC session description for one data channel, unordered and never resent, that
   * carries this client's `inputs` up and its snapshots down. The gateway answers it with an `rtc` of its own; the match
   * never sees it.
   */
  rtc: struct({ sdp: t.string }),
});
export type ClientMessage = Infer<typeof ClientMessage>;

/** The UDP lane's timing (ADR-0178), the same on both sides. */
export const LANE = {
  /**
   * A lane carries a connection while it brought something within this long: the client's inputs (one every tick) to
   * the gateway, its snapshots (one every tick) to the client. Past it each side falls back to the socket for what it
   * sends, until the lane brings something again.
   */
  liveMs: 500,
} as const;

/** A client message's type from its first byte alone, or null: a gateway routes on it without decoding the rest. */
export function clientMessageType(bytes: Uint8Array): ClientMessage['type'] | null {
  return (ClientMessage.names[bytes[0] ?? 255] as ClientMessage['type'] | undefined) ?? null;
}

/**
 * A room chat's budget (ADR-0170): a line at most `chars` long (a longer one kicks its sender: the client never sends one),
 * `burst` lines at once and one more every `everyS` seconds (more are dropped: they would only flood everyone's screen).
 */
export const ROOM_CHAT = { chars: 100, burst: 3, everyS: 2 } as const;

/** A room chat line as it is sent and heard (ADR-0170): one line, no control characters, trimmed, at most `ROOM_CHAT.chars`. */
export const chatLine = (text: string): string =>
  text
    .replace(/\p{Cc}/gu, ' ')
    .trim()
    .slice(0, ROOM_CHAT.chars);

/**
 * A client's `diag` budget (ADR-0126): at most `chars` characters a line, `burst` lines at once and one more a second.
 * Past it the match kicks the client: every message goes onto the match's tape, and a flood would only swell it.
 */
export const DIAG = { chars: 1024, burst: 10 } as const;

/**
 * A `diag` line (ADR-0126), JSON: untrusted, like everything a client sends, and read only by the replay tools.
 * `client` once a session (the device and the window), `perf` every `DIAG_EVERY_S` of a session (frame rate, the
 * worst frame and hitches over 50 ms, the last round trip, corrections and starved snapshots since the last one, KB/s
 * in, the tab hidden, the UDP lane carrying its snapshots), `error` for each error the page hit (the first few).
 */
export type DiagNote =
  | {
      k: 'client';
      build: string;
      renderer: string;
      gpu: string;
      agent: string;
      viewport: string;
      ui: number;
      pad: boolean;
      touch: boolean;
    }
  | {
      k: 'perf';
      fps: number;
      worstMs: number;
      long: number;
      /** The ping shown (the game's round trip, `NetStats.rttLastMs`); before 0.42.1: the socket's. */
      rttMs: number;
      /** The socket's last ping, held up behind a resend on a lossy line (ADR-0178 amended); none on older tapes. */
      socketMs?: number;
      corrections: number;
      starved: number;
      /** Snapshots it couldn't lay on the one they were written against (ADR-0204: 0 unless broken); none on older tapes. */
      unlaid?: number;
      kbIn: number;
      hidden: boolean;
      /** Its snapshots came on the UDP lane (ADR-0178) as the note was written, not on the socket. */
      lane: boolean;
      /** The input lead it aimed at (ms: the base and what starving added, `NetStats.inputLead`); none on older tapes. */
      leadMs?: number;
    }
  | { k: 'error'; source: string; message: string; stack: string };

/** Seconds between two `perf` notes. */
export const DIAG_EVERY_S = 5;
export type HubAction = Extract<ClientMessage, { type: 'hub' }>['action'];
export type ZombieOrderAction = Extract<ClientMessage, { type: 'zombie' }>['action'];

// ─── Server → client ────────────────────────────────────────────────────────────────────────────────

export const EntityFlag = {
  Dead: 1,
  Reloading: 2,
  Attacking: 4,
  Spinning: 8,
  Mega: 16,
  Returning: 32,
  /** Beetle: charging / dazed after ramming a wall. */
  Charging: 64,
  /** Dazed: a beetle that rammed a wall; a player, minion or creep punched by a Robo Fist (ADR-0039). */
  Stunned: 128,
  /** Frosted (slowed) by a Frostbite hit (ADR-0021/31): players, minions, creeps, bosses, scarecrows. */
  Frozen: 256,
  /** A Hedgehog rolled up into a ball: nothing hurts it, bullets come back as spines (ADR-0053). */
  Curled: 512,
  /**
   * Players: the authority put them somewhere within the last `TELEPORT_FLAGGED` ticks (`teleport`); remote views snap
   * instead of lerping from a snapshot without the flag to one with it.
   */
  Teleported: 2048,
  /** A Lantern its moths still guard: every hit on it is blocked (ADR-0067). */
  Shielded: 4096,
  /** A zombie climbing out of its grave or its tunnel (ADR-0098). */
  Rising: 16384,
  /** A Digger tunnelling: a mound of earth on the move, nothing hits it (ADR-0098). */
  Burrowed: 32768,
  /** A Bloater burning its fuse (ADR-0098). A Horror winding up its slam is `Charging`. */
  Fused: 65536,
  /** A player at an RC tractor's controls (ADR-0101): standing still, shielded from all harm. */
  Driving: 131072,
  /** A player holding a Shield up (ADR-0109). */
  Blocking: 262144,
  /** A player down but not out (a battle royale's duos, ADR-0120): crawling, bleeding, waiting for a mate. */
  Downed: 524288,
  /** A Snapdragon whose next attack is a ring of seeds all round, not a fan at its aim (ADR-0067 family). */
  Ring: 1048576,
  /** An armoured zombie whose armour a Heavy Shell knocked off for good (ADR-0171): hurt by everything now. */
  Cracked: 2097152,
  /**
   * A shrouded zombie (a Wisp) towers and bees see: in a Sunflower's Daylight (ADR-0171), or for good once the soak of a
   * Sprinkler or the honey of a hive that sees Wisps touched it (ADR-0227).
   */
  Revealed: 4194304,
  /**
   * A zombie frozen solid by a Sprinkler's freezing spray (ADR-0171: Ice Sprinkler, Blizzard): stunned in a block of ice.
   * Never a Geyser's or a Giant Pumpkin's stun (`Stunned` alone).
   */
  Iced: 8388608,
  /**
   * ADR-0184: a Sunflower a Moth sits on (smothered: it fires nothing), and the Moth sitting on it; a Mortar or a
   * Beehive whose dead in reach no eye of the party sees (`Blind`).
   */
  Smothered: 16777216,
  Blind: 33554432,
  /** A gardener holding the crypt's key (a battle royale's, ADR-0202): its squad may go into the crypt. */
  Key: 67108864,
  /** A gardener hidden in a bush (ADR-0193): sent to its squad, and to another only from beside the bush. */
  Hidden: 134217728,
  /**
   * A gardener planting a fallen mate's seed at a seedbed (feedback #27, ADR-0203): whoever sees them sees the bed light
   * up and grow, foes too (how far it has got is their squad's alone: the seed's `plant`).
   */
  Planting: 268435456,
} as const;

/** Snapshots a teleported player stays `Teleported` in (ADR-0178): the UDP lane may lose the first ones there. */
export const TELEPORT_FLAGGED = 4;

/**
 * Ticks from one snapshot to the next (ADR-0204): 30 a second at either pace, so a Zombie Nights run at double speed
 * (ADR-0173: `speed` 2) sends one every other tick, with the events of the tick between.
 */
export const snapshotEvery = (speed: number): number => Math.max(1, Math.round(speed));

/** Active power-ups of a player (`player.buffs`). */
export const BuffFlag = { Speed: 1, Rage: 2, Shield: 4 } as const;

export const EntityState = taggedUnion('kind', {
  player: struct({
    id: t.varuint,
    team: t.u8,
    x: pos,
    y: pos,
    aim: t.angle16,
    /** Where its sight cone points (the sim's `Aim.look`, ADR-0010 amended): its mates' fog opens there. */
    look: t.angle16,
    hp: t.varuint,
    /** Max health: more than a gardener's with Bark Armour (ADR-0141). */
    maxHp: t.varuint,
    flags: t.varuint,
    weapon: t.u8,
    buffs: t.u8,
    /**
     * Shield HP left — an Leaf Shield's plus a Katana's guard (ADR-0031/0035; 0 = none): the charcoal part of the
     * health bar over the player.
     */
    shield: t.u8,
    /** Down (flag Downed) in a Zombie Nights party (ADR-0181): how far their mates' circle has filled, in 255ths. */
    revive: t.u8,
    /** On its way through the air (ADR-0198; none on its feet): content FlightKind `kind`, `h` px up (a launch's, ADR-0200, past 255). */
    air: optional(struct({ kind: t.u8, h: t.u16 })),
    /**
     * Its fishing line out (ADR-0229; none without one): content `FishPhase` `phase`; the float or the fish at (x, y), `z`
     * px up; `tier` of what is on it (content `FishTier`); the line's `tension` in 255ths of where it snaps, `left` px of
     * line still out; `flags` content `FishFlag` (a run warned, a run pulling, reeling, a leap).
     */
    fish: optional(
      struct({ phase: t.u8, x: pos, y: pos, z: t.u8, tier: t.u8, tension: t.u8, left: t.u16, flags: t.u8 }),
    ),
    /** The damage it has dealt the lobby island's dummies while warmed up on (ADR-0190 amended: TOP SHOTS); 0 after. */
    shots: t.varuint,
  }),
  scarecrow: struct({ id: t.varuint, x: pos, y: pos, hp: t.varuint, flags: t.varuint }),
  minion: struct({ id: t.varuint, team: t.u8, x: pos, y: pos, hp: t.varuint, maxHp: t.varuint, flags: t.varuint }),
  base: struct({ id: t.varuint, team: t.u8, x: pos, y: pos, hp: t.varuint, maxHp: t.varuint, flags: t.varuint }),
  /** Mini-boss (`bossKind` = BossKind): `windup` = ticks until its attack, aimed at (tx, ty) (0 = none). */
  boss: struct({
    id: t.varuint,
    bossKind: t.u8,
    x: pos,
    y: pos,
    hp: t.varuint,
    maxHp: t.varuint,
    windup: t.u8,
    tx: pos,
    ty: pos,
    flags: t.varuint,
  }),
  /**
   * A jungle creep (`creepKind` = CreepKind, ADR-0019); flags Attacking (its attack pose), Returning (going home),
   * Curled (a Hedgehog rolled up, ADR-0053), Shielded (a Lantern its moths guard, ADR-0067). `hoard`: the gold in a
   * magpie's nest (0 for every other creature, ADR-0067).
   */
  creep: struct({
    id: t.varuint,
    creepKind: t.u8,
    x: pos,
    y: pos,
    hp: t.varuint,
    maxHp: t.varuint,
    flags: t.varuint,
    hoard: t.varuint,
  }),
  /** A vision ward of `team` planted by `owner` (a player's NetId, ADR-0067): `expires` = ticks until it burns out. */
  ward: struct({ id: t.varuint, team: t.u8, owner: t.varuint, x: pos, y: pos, hp: t.varuint, expires: t.varuint }),
  /**
   * An RC tractor of `team` driven by `owner` (a player's NetId, ADR-0101), heading `angle`; `weapon` is whose `tractor` it
   * is (the RC Tractor, a duck or a duckling of the Duck Family, ADR-0159).
   */
  tractor: struct({
    id: t.varuint,
    team: t.u8,
    owner: t.varuint,
    weapon: t.u8,
    x: pos,
    y: pos,
    angle: t.angle16,
    hp: t.varuint,
    maxHp: t.varuint,
  }),
  /** A Shield of `team` thrown by `owner` (a player's NetId, ADR-0109); flags Returning on its way home. */
  disc: struct({ id: t.varuint, team: t.u8, owner: t.varuint, x: pos, y: pos, flags: t.varuint }),
  /**
   * A battle royale's beacon rocket of `team` sent by `owner` (a player's NetId, ADR-0220 amended): over (x, y), `z` px
   * up, nosed `angle` as drawn (its drone: the way it goes), in `phase` (content `BeaconPhase`: kicked out cold, flying
   * lit, its drone out following its prey while its squad sees round it, its battery dying). Every squad is sent it: it
   * is in the sky.
   */
  beacon: struct({
    id: t.varuint,
    team: t.u8,
    owner: t.varuint,
    x: pos,
    y: pos,
    z: pos,
    angle: t.angle16,
    phase: t.u8,
  }),
  /** A Hive Launcher's bee of `team` (ADR-0078); flags Returning in its last moments (it fades). */
  bee: struct({ id: t.varuint, team: t.u8, x: pos, y: pos, flags: t.varuint }),
  /**
   * A breakable crate (ADR-0068), `variant` = CrateKind: a balloon's basket `falling` for that many more ticks (0 = down:
   * solid, the predictor bumps into it); a battle royale's chest's lock `open` of 255 full (ADR-0190).
   */
  crate: struct({
    id: t.varuint,
    variant: t.u8,
    x: pos,
    y: pos,
    hp: t.varuint,
    maxHp: t.varuint,
    falling: t.varuint,
    open: t.u8,
  }),
  /**
   * A gun card out of crate `group` (its NetId, ADR-0068): its `loot`, gone in `expires` ticks; `team` broke the crate and
   * sees the card through the fog.
   */
  card: struct({ id: t.varuint, x: pos, y: pos, loot: t.u8, group: t.varuint, expires: t.varuint, team: t.u8 }),
  /**
   * A fallen gardener `owner`'s seed (a battle royale's, feedback #27), sent to their squad `team` alone: lying, gone in
   * `expires` ticks, or carried by `carrier` (a NetId, 0 = nobody); `plant`: ticks of its planting at a seedbed so far.
   */
  seed: struct({
    id: t.varuint,
    team: t.u8,
    owner: t.varuint,
    carrier: t.varuint,
    x: pos,
    y: pos,
    expires: t.varuint,
    plant: t.u16,
  }),
  /**
   * A pickup (a battle royale's, ADR-0189): content PickupKind `pickupKind` giving `amount`; `group` the NetId of the
   * mini chest it burst out of (0: none), where it flies from.
   */
  pickup: struct({ id: t.varuint, pickupKind: t.u8, amount: t.u8, x: pos, y: pos, group: t.varuint }),
  /**
   * A zombie (ADR-0098), `zombieKind` = ZombieKind; flags Attacking (its swing, a spit, the Thresher's blades), Rising,
   * Burrowed, Fused, Charging (the Horror winding up, the Gravedigger digging), Frozen, Stunned. `facing`: where it
   * faces (256ths of a turn: an armoured front, content `ZombieGuard`); `gauge`: its kind's own — a rooted Blight's rot
   * (px across), a Moon Priest's altars standing. A part of the Buried King (ADR-0164: a fist, his head) is Burrowed while
   * it is up out of reach, its `gauge` then the ticks until it lands (0: it isn't coming down) and Charging once it has
   * stopped over its spot; Rising while he climbs out; lying on the ground its `gauge` is the ticks it stays; a fist's
   * `facing` is its side (0 his right). An ox of the Yoke's, or the pair under its beam (ADR-0165): Charging with its
   * horns down (`gauge`: the ticks until it runs down its `facing`) and on its run (`gauge` 0, Attacking too); stopped
   * (winded at the floor's edge, or Stunned: run into the other, its guard down) its `gauge` is the ticks it stays so.
   */
  zombie: struct({
    id: t.varuint,
    zombieKind: t.u8,
    x: pos,
    y: pos,
    hp: t.varuint,
    maxHp: t.varuint,
    flags: t.varuint,
    facing: t.u8,
    gauge: t.varuint,
  }),
  /**
   * A build (ADR-0098), `buildKind` = BuildKind, bought by `owner` (a NetId): a tower (ADR-0171: `tower` its TowerKind,
   * 255 = none: a fence) at `tier` (content `towerTier`: 0 as bought, 1-3 path A's, 4-6 path B's, 7 a Paragon; and
   * `CROSS`, 8, over 1-6: the other path's first upgrade too, ADR-0227; its reach and all its numbers are content
   * `towerStats`'), where its head points (`aim`) and what at (`target`, 0 = nothing); `value`: what
   * selling it now gives; `paid`: the gold in it (its upgrades' too; a Paragon's, ADR-0181, its degree: `paragonDegree`);
   * `flags`: Smothered (a Sunflower a Moth sits on), Blind (a Mortar or a hive no eye spots for; ADR-0184).
   */
  build: struct({
    id: t.varuint,
    buildKind: t.u8,
    x: pos,
    y: pos,
    hp: t.varuint,
    maxHp: t.varuint,
    tower: t.u8,
    tier: t.u8,
    aim: t.angle16,
    target: t.varuint,
    owner: t.varuint,
    value: t.varuint,
    paid: t.varuint,
    flags: t.varuint,
  }),
  /**
   * A battle royale's car (ADR-0214): `variant` its CarKind, facing `heading`; its squad (`team`, its riders'; 0: nobody's);
   * who sits where (`s0` … `s3`, NetIds, 0: empty; `s0` at the wheel); its front wheels' `steer` (-1 … 1 in 127ths);
   * `flags` CarFlag bits.
   */
  car: struct({
    id: t.varuint,
    variant: t.u8,
    x: pos,
    y: pos,
    heading: t.angle16,
    team: t.u8,
    hp: t.varuint,
    maxHp: t.varuint,
    flags: t.varuint,
    steer: t.i8,
    s0: t.varuint,
    s1: t.varuint,
    s2: t.varuint,
    s3: t.varuint,
  }),
});
export type EntityState = Infer<typeof EntityState>;

/**
 * A car's flags (ADR-0214): `Sliding` (a drift: its tyres leave marks), `Parked` (standing still on the wire's grid: a
 * client's predictor stands its box there), `Wreck` (burnt out, lying in the way).
 */
export const CarFlag = { Sliding: 1, Parked: 2, Wreck: 4 } as const;
export type EntityKind = EntityState['kind'];

/**
 * An entity that changed since the snapshot a delta is written against (ADR-0204): its kind, its id and only the fields
 * that look different on the wire, a position or an angle as its step (its struct's `change`). Written as its kind's
 * tag, its id and the struct's `writeChange` (the id aside).
 */
export interface EntityChange {
  kind: EntityKind;
  id: number;
  fields: Partial<Omit<EntityState, 'kind' | 'id'>>;
}

/** The struct an entity kind is written with. */
export const entityCodec = (kind: EntityKind) => EntityState.variants[kind] as unknown as StructCodec<EntityShape>;
type EntityShape = Record<string, Codec<unknown>>;

const EntityChange: Codec<EntityChange> = {
  write(w, c) {
    w.u8(EntityState.names.indexOf(c.kind));
    w.varuint(c.id);
    entityCodec(c.kind).writeChange(w, c.fields, 1);
  },
  read(r) {
    const kind = EntityState.names[r.u8()];
    if (kind === undefined) throw new ProtocolError('Unknown entity kind');
    return { kind, id: r.varuint(), fields: entityCodec(kind).readChange(r, 1) };
  },
};

/**
 * Exact (f64) state of the recipient's own player for bit-exact reconciliation.
 * `lastButtons` = the buttons the server actually used for `ack` (they differ from what the client sent
 * when the server had to guess on starvation).
 */
/**
 * A battle royale's explosives in hand (ADR-0194, ADR-0201) in the two bytes the grenades took alone: how many (five
 * bits), and which kind (content `Explosive`, an Ejector Seat too: ADR-0218, beacons: ADR-0220, gramophones: ADR-0221) in
 * the first's top three bits; ticks till the next throw, and whether a C4 charge is out in the second's (a snapshot's
 * bytes are budgeted: `netcode.test.ts`).
 */
const explosives: Codec<{ grenades: number; explosive: number; grenadeCd: number; charge: boolean }> = {
  write(w, v) {
    w.u8((v.grenades & 0x1f) | ((v.explosive & 7) << 5));
    w.u8((v.grenadeCd & 0x7f) | (v.charge ? 0x80 : 0));
  },
  read(r) {
    const [a, b] = [r.u8(), r.u8()];
    return { grenades: a & 0x1f, explosive: a >> 5, grenadeCd: b & 0x7f, charge: (b & 0x80) !== 0 };
  },
};

export const SelfState = struct({
  x: t.f64,
  y: t.f64,
  vx: t.f64,
  vy: t.f64,
  hp: t.varuint,
  weapon: t.u8,
  ammo: t.u8,
  reserve: t.u16,
  /** Ticks left on a picked-up gun (ADR-0025; 0 = the default gun, never runs out). */
  gunTime: t.u16,
  cooldown: t.u8,
  reload: t.u16,
  spin: t.u8,
  lastButtons: t.varuint,
  dead: t.bool,
  respawnIn: t.u16,
  gold: t.varuint,
  damageLevel: t.u8,
  /** Power-up ticks left (speed is simulated by the predictor) and what the leaf shield still absorbs. */
  speedTicks: t.u16,
  rageTicks: t.u16,
  shieldTicks: t.u16,
  shieldHp: t.u8,
  /** Ticks left frosted (Frostbite, ADR-0021/31): the predictor slows you for exactly as long as the server. */
  frozenTicks: t.u8,
  /**
   * How often the server moved you (a respawn, a new round, a team move; mod 256): when it changes the predictor snaps
   * to the new spot rather than counting a correction.
   */
  teleports: t.u8,
  /** Ticks left dazed by a Robo Fist punch (ADR-0039): the predictor keeps you still and silent exactly as long. */
  stunTicks: t.u8,
  /** A knock on you (ADR-0035): thrown at (knockX, knockY) px/s for `knockTicks` more, slowing by `knockDecay`. */
  knockX: t.f64,
  knockY: t.f64,
  knockTicks: t.u8,
  knockDecay: t.f64,
  knockSlam: t.bool,
  /** A Katana dash in progress (ADR-0035): ticks left, its angle and speed (speed stays set the tick after). */
  dashTicks: t.u8,
  dashAngle: t.f64,
  dashSpeed: t.f64,
  /** What is left of your gun's guard (the Katana, ADR-0035): shield HP on top of an Leaf Shield's. */
  guard: t.u8,
  /** Ticks until your gun's second fire is ready (the Mecha Suit's rockets, ADR-0096). */
  alt: t.u16,
  /**
   * A battle royale's grenades left and ticks till the next throw (ADR-0194: predicted, as the gun's cooldown is); which
   * explosive they are and whether a C4 charge of yours is out (ADR-0201). Two bytes (`explosives`).
   */
  explosives,
  /** Where your Magic Wand's storm is (ADR-0097): it chases the crosshair. */
  stormX: t.f64,
  stormY: t.f64,
  /** The gun under your transformation (ADR-0108) and its magazine, pocket and clock: the predictor gives it back too. */
  under: t.u8,
  underAmmo: t.u8,
  underReserve: t.u16,
  underTime: t.u16,
  /** Your max health. */
  maxHp: t.varuint,
  /**
   * Down, not out (a battle royale's duos, ADR-0120): the predictor crawls you and holds your fire; `revive`: ticks of
   * the revive you are in so far (yours, or the mate's you are getting up).
   */
  downed: t.bool,
  revive: t.u16,
  /**
   * Zombie Nights' cards (ADR-0098): your level of each stat (content PERKS order; the predictor plays your fire rate and
   * pace by them), the cards on offer (`offer`: content CardOffer) and what they are (`cards`: weapon ids or PerkKinds).
   */
  perks: array(t.u8),
  offer: t.u8,
  cards: array(t.u8),
  /**
   * Zombie Nights (ADR-0184): the day your account had reached in any run before this one (65535: every tower): with the
   * run's day it says which towers are on sale to you (content `towerUnlocked`).
   */
  reached: t.u16,
  /**
   * Your way through the air (ADR-0198, the sim's `Flight`; none on your feet): the predictor flies you on exactly as
   * the server does. `airOn`: the mushroom you stand on, + 1 (0: none).
   */
  air: optional(
    struct({
      kind: t.u8,
      t: t.u16,
      ticks: t.u16,
      x0: t.f64,
      y0: t.f64,
      x1: t.f64,
      y1: t.f64,
      top: t.f64,
      h: t.f64,
      of: t.u16,
      vx: t.f64,
      vy: t.f64,
      chute: t.f64,
    }),
  ),
  airOn: t.varuint,
  /**
   * Where your sight cone points and what is left of its stamina (the sim's `Aim.look`, `Aim.stamina`: it turns after your
   * aim as `ROYALE.sight` says), and the bush you hide in (`Player.bush`, ADR-0193; 0: none), which turns it slowly.
   */
  look: t.f64,
  stamina: t.f64,
  bush: t.varuint,
  /**
   * Your seat in a car (ADR-0214, the sim's `Ride`; none on foot: `air`'s kind says `FlightKind.Car` while you sit in
   * one): which car (a NetId), the seat (0: at the wheel) and its kind; at the wheel the predictor drives it on exactly as
   * the server does: its heading (`hx`, `hy`), its front wheels' `steer`, whether it slides.
   */
  ride: optional(
    struct({
      car: t.varuint,
      seat: t.u8,
      kind: t.u8,
      hx: t.f64,
      hy: t.f64,
      steer: t.f64,
      slide: t.bool,
    }),
  ),
});
export type SelfState = Infer<typeof SelfState>;

/** What landed a killing blow (the kill feed, ADR-0031). */
const KillerKind = t.enumOf(['player', 'boss', 'creep', 'none', 'zombie'] as const);

export const NetEvent = taggedUnion('type', {
  /**
   * `power`: a charged shot's charge in ticks (the Railgun, ADR-0035), a lob's flight (`LOB_STEP`s, ADR-0109), a
   * Sprinkler's spray's kind (content `SprayKind`: plain, geyser, freezing; ADR-0171), else 0.
   */
  shot: struct({ shooter: t.varuint, seq: t.varuint, weapon: t.u8, x: pos, y: pos, angle: t.angle16, power: t.u8 }),
  hit: struct({
    target: t.varuint,
    shooter: t.varuint,
    seq: t.varuint,
    damage: t.varuint,
    blocked: t.varuint,
    x: pos,
    y: pos,
    angle: t.angle16,
    killed: t.bool,
    /** A Lucky Clover's critical hit (ADR-0141). */
    crit: t.bool,
    /** It took the last of the target's shield: the shield broke (ADR-0189). */
    broke: t.bool,
    /** What it was dealt with (the death recap); NO_WEAPON for a swing, a bite, a slam. */
    weapon: t.u8,
  }),
  projectileEnd: struct({
    shooter: t.varuint,
    seq: t.varuint,
    x: pos,
    y: pos,
    nx: t.i8,
    ny: t.i8,
    reason: t.enumOf(['wall', 'range'] as const),
  }),
  reloadStart: struct({ entity: t.varuint }),
  reloadEnd: struct({ entity: t.varuint }),
  dryFire: struct({ entity: t.varuint }),
  heave: struct({ entity: t.varuint }),
  respawn: struct({ entity: t.varuint, x: pos, y: pos }),
  melee: struct({ attacker: t.varuint, target: t.varuint }),
  weaponChange: struct({ entity: t.varuint, weapon: t.u8, reason: t.enumOf(['empty', 'expired', 'used'] as const) }),
  round: struct({ phase: t.enumOf(['start', 'end'] as const), winner: t.u8 }),
  gold: struct({
    entity: t.varuint,
    amount: t.varuint,
    x: pos,
    y: pos,
    via: t.enumOf(['minion', 'boss', 'kill', 'creep', 'hoard', 'zombie', 'dawn', 'refund', 'crate'] as const),
  }),
  gunDrop: struct({ entity: t.varuint, owner: t.varuint, loot: t.u8, x: pos, y: pos }),
  bossSpawn: struct({ entity: t.varuint, kind: t.u8, x: pos, y: pos }),
  slam: struct({ entity: t.varuint, x: pos, y: pos }),
  bossDown: struct({
    entity: t.varuint,
    kind: t.u8,
    winner: t.varuint,
    weapon: t.u8,
    damage: t.varuint,
    total: t.varuint,
    gold: t.varuint,
    x: pos,
    y: pos,
  }),
  explosion: struct({ shooter: t.varuint, seq: t.varuint, x: pos, y: pos, radius: t.u8 }),
  /** A Hive Launcher's hive burst into bees (ADR-0078). */
  swarm: struct({ shooter: t.varuint, seq: t.varuint, x: pos, y: pos }),
  /** A Toxic Launcher's canister burst into a toxic puddle (ADR-0082); a Fire Bow's arrow (`weapon`) into fire (ADR-0109). */
  puddle: struct({
    shooter: t.varuint,
    seq: t.varuint,
    team: t.u8,
    weapon: t.u8,
    x: pos,
    y: pos,
    radius: t.u8,
    ticks: t.varuint,
  }),
  /** A thrown Shield glanced off something, or its owner caught it (ADR-0109). */
  disc: struct({ entity: t.varuint, owner: t.varuint, x: pos, y: pos, phase: t.enumOf(['bounce', 'catch'] as const) }),
  /** A Buzz Saw parried a shot (ADR-0078). */
  parry: struct({ entity: t.varuint, shooter: t.varuint, seq: t.varuint, x: pos, y: pos, angle: t.angle16 }),
  /** A Poison Dart's poison on `target`: its stacks now, and how long it bites unless hit again (ADR-0083). */
  poison: struct({ target: t.varuint, shooter: t.varuint, stacks: t.varuint, ticks: t.varuint }),
  powerup: struct({ entity: t.varuint, kind: t.u8, x: pos, y: pos }),
  bossCast: struct({
    entity: t.varuint,
    attack: t.enumOf(['volley', 'burst', 'bite', 'charge', 'crash'] as const),
    x: pos,
    y: pos,
  }),
  built: struct({ entity: t.varuint, owner: t.varuint, team: t.u8, x: pos, y: pos, first: t.bool }),
  bossLeave: struct({ entity: t.varuint, kind: t.u8, x: pos, y: pos }),
  strike: struct({ owner: t.varuint, team: t.u8, weapon: t.u8, x: pos, y: pos, x2: pos, y2: pos, ticks: t.varuint }),
  nuke: struct({ owner: t.varuint, weapon: t.u8, x: pos, y: pos, radius: t.u8 }),
  kill: struct({ killer: t.varuint, victim: t.varuint, by: KillerKind, weapon: t.u8, assists: array(t.varuint) }),
  /** The Robo Fist (ADR-0035): its claw caught `target`, reeled in over `ticks`; then the punch; wall slams. */
  hooked: struct({ shooter: t.varuint, target: t.varuint, seq: t.varuint, ticks: t.u8 }),
  abduct: struct({
    shooter: t.varuint,
    target: t.varuint,
    seq: t.varuint,
    x: pos,
    y: pos,
    tx: pos,
    ty: pos,
    ticks: t.u8,
  }),
  punch: struct({ shooter: t.varuint, target: t.varuint, seq: t.varuint, x: pos, y: pos, angle: t.angle16 }),
  splat: struct({ entity: t.varuint, x: pos, y: pos, nx: t.unit8, ny: t.unit8 }),
  /** A fully charged rail burnt a tunnel (ADR-0038): these cells (`cy * width + cx`) are open now. */
  carve: struct({ shooter: t.varuint, seq: t.varuint, cells: array(t.varuint) }),
  /** `by` (of `team`) pinged (x, y) on the map (ADR-0069); only `team` is sent it. */
  signal: struct({ by: t.varuint, team: t.u8, x: pos, y: pos }),
  /** Magpie `by` pecked player `entity` at (x, y) and stole `amount` gold into its nest (ADR-0067). */
  stolen: struct({ entity: t.varuint, by: t.varuint, amount: t.varuint, x: pos, y: pos }),
  /** `by` (of `team`) took a Magpie nest's hoard at (x, y): `amount` gold (ADR-0067). */
  hoard: struct({ by: t.varuint, team: t.u8, amount: t.varuint, x: pos, y: pos }),
  /** A Lantern burst at (x, y) and planted vision ward `entity` for `team`, credited to `by` (0 = nobody; ADR-0067). */
  warded: struct({ entity: t.varuint, by: t.varuint, team: t.u8, x: pos, y: pos }),
  /** Battle royale squad `team` has nobody standing: it is out of the round (ADR-0120). */
  out: struct({ team: t.u8 }),
  /** Down, not out (a battle royale's duos, ADR-0120): put down as a kill would be; and back up, by a mate. */
  down: struct({ killer: t.varuint, victim: t.varuint, by: KillerKind, weapon: t.u8 }),
  revive: struct({ entity: t.varuint, by: t.varuint }),
  /** A fallen gardener grew back at a seedbed, planted by a mate (a battle royale's, feedback #27). */
  regrow: struct({ entity: t.varuint, by: t.varuint }),
  /** Gardener `entity` walked over pickup `pickup` (content PickupKind `kind`), given `amount` (ADR-0189). */
  pickup: struct({ entity: t.varuint, pickup: t.varuint, kind: t.u8, amount: t.u8, x: pos, y: pos }),
  /**
   * Stone altar `altar` (`map.altars`) woken by `entity`, its guardian risen, lost, won (its Epic gun dropped at
   * (x, y) for `entity`'s squad, ADR-0215; 0: for anyone), or ready (ADR-0189, amended by ADR-0202).
   */
  altar: struct({
    altar: t.u8,
    phase: t.enumOf(['wake', 'rise', 'fail', 'won', 'ready'] as const),
    entity: t.varuint,
    x: pos,
    y: pos,
  }),
  /**
   * MOST WANTED (ADR-0229): `entity` took the bounty off wanted board `poster` at (x, y), survived it, or was knocked out
   * first (its gold chest at (x, y)).
   */
  wanted: struct({
    phase: t.enumOf(['start', 'won', 'lost'] as const),
    entity: t.varuint,
    poster: t.u8,
    x: pos,
    y: pos,
  }),
  /**
   * Crate `entity` (CrateKind `variant`) landed, was broken by `by` (0: not a player; an apple crate `heal`ed them) or grew
   * back (ADR-0068).
   */
  crate: struct({
    entity: t.varuint,
    variant: t.u8,
    phase: t.enumOf(['landed', 'broken', 'grown'] as const),
    by: t.varuint,
    heal: t.u8,
    x: pos,
    y: pos,
  }),
  /** Player `entity` took card `card` (its `loot`) out of crate `group`; the group's other cards are gone (ADR-0068). */
  taken: struct({ entity: t.varuint, card: t.varuint, group: t.varuint, loot: t.u8, x: pos, y: pos }),
  /** Zombie Nights (ADR-0098): night `day` falls, or dawn breaks after it. */
  night: struct({ phase: t.enumOf(['night', 'dawn'] as const), day: t.varuint }),
  /**
   * A zombie's move worth showing (ADR-0098): its grave, a spit, a slam (lands in `ticks`), a call, its tunnel, a fuse; a
   * Gravecaller's call, a Gravedigger's dig (a grave opens in `ticks`), a grave opened or closed, a Blight taking root.
   * The Buried King's (ADR-0164): `wake` (the party is in the Barrow in `ticks`), `roar`, `rain` and `crack` (a tombstone
   * on (x, y), a strip of the floor about `x`: it lands in `ticks`), `smash` (a fist or his head came down). The Yoke's
   * (ADR-0165): `charge` (an ox starts its run from (x, y)), `skid` (it stops at the floor's edge), `crash` (two met),
   * `snap` (the beam broke), `furrow` (its run's furrow, from where its `charge` was to (x, y), burns for `ticks`). A
   * hearse's `spill` (ADR-0168: it stopped for good at (x, y), its cargo climbs out). Chomper's `crush` (ADR-0180: it
   * crushed build `entity` at (x, y)). ADR-0184: a Digger's `soaked` (forced up by wet ground at (x, y), dazed for
   * `ticks`), a Moth's `smother` (it landed on the Sunflower at (x, y)). ADR-0226, the weekly deck's variants: a
   * Mossback's `regrow`, a bee's `doze` in a Beekeeper's smoke (`entity` the bee), a Bone Balloon's `pop` (its cargo drops
   * at (x, y)), a Grave Robber's `dugout`, a Sapper's `plant` (his keg at (x, y) goes up in `ticks`) and `fizzle`, a Fence
   * Thief's `steal` (the fence at (x, y), its tier in `ticks`).
   */
  zombieCast: struct({
    entity: t.varuint,
    cast: t.enumOf([
      'rise',
      'spit',
      'slam',
      'land',
      'summon',
      'surface',
      'fuse',
      'burst',
      'dig',
      'sweep',
      'root',
      'altar',
      'unward',
      'lob',
      'aim',
      'snipe',
      'wake',
      'roar',
      'rain',
      'crack',
      'smash',
      'charge',
      'skid',
      'crash',
      'snap',
      'furrow',
      'spill',
      'crush',
      'soaked',
      'smother',
      'regrow',
      'doze',
      'pop',
      'dugout',
      'plant',
      'fizzle',
      'steal',
    ] as const),
    x: pos,
    y: pos,
    ticks: t.varuint,
  }),
  /** Build `entity` (BuildKind `kind`, a tower's TowerKind `tower`, 255 a fence) sold by `owner` for `gold` (ADR-0098). */
  sold: struct({ entity: t.varuint, owner: t.varuint, kind: t.u8, tower: t.u8, gold: t.varuint, x: pos, y: pos }),
  /**
   * Tower `entity` stands as its Paragon (ADR-0181: `first`), or its Paragon ate again, by `owner`'s order: it ate the
   * towers `eaten` (each where it stood) and is of degree `degree`.
   */
  paragon: struct({
    entity: t.varuint,
    owner: t.varuint,
    eaten: array(struct({ entity: t.varuint, x: pos, y: pos })),
    degree: t.u8,
    first: t.bool,
  }),
  /**
   * A Zombie Nights order of player `entity` settled (ADR-0098): `item` the shop item, the card taken, or an upgrade's
   * path (0: A, 1: B; ADR-0171); `fence`: an upgrade of a fence (amended).
   */
  order: struct({
    entity: t.varuint,
    action: t.enumOf(['buy', 'place', 'sell', 'pick', 'upgrade', 'paragon'] as const),
    item: t.u8,
    ok: t.bool,
    fence: t.bool,
  }),
  /** Zombie Nights' meteors (ADR-0186): a meteor lands at (x, y) in `ticks`. */
  meteor: struct({ x: pos, y: pos, ticks: t.varuint }),
  /** Zombie Nights' hell (ADR-0187): gardener `entity` fell into a pit of lava at (x, y), or (`out`) climbed out there. */
  lava: struct({ entity: t.varuint, x: pos, y: pos, out: t.bool }),
  /** Thing `thing` of the map (ADR-0188: a barrel, a woodpile) broke: it stops nothing from now on. */
  thingBroke: struct({ thing: t.varuint }),
  /** Player `player` crawled into the bush whose foot is (x, y), or out of it (`into` false) (ADR-0193). */
  bush: struct({ player: t.varuint, x: pos, y: pos, into: t.bool }),
  /** Player `player` went into the crypt (`into`, ADR-0202) or out of it, coming out at (x, y). */
  crypt: struct({ player: t.varuint, into: t.bool, x: pos, y: pos }),
  /** Player `player` took the stairs (ADR-0196) at (x, y) and came out at (tx, ty), `down` or up. */
  stairs: struct({ player: t.varuint, x: pos, y: pos, tx: pos, ty: pos, down: t.bool }),
  /**
   * Player `shooter` threw grenade `seq` (ADR-0194), or a C4 charge (ADR-0201: content `Explosive` `kind`), off (x, y) `z`
   * px up at (vx, vy, vz) px/s (the sim quantized them).
   */
  throw: struct({
    shooter: t.varuint,
    seq: t.varuint,
    kind: t.u8,
    x: pos,
    y: pos,
    z: pos,
    vx: pos,
    vy: pos,
    vz: pos,
  }),
  /** Player `shooter` pressed to set off their C4 charge (ADR-0201): it goes off as soon as it is armed. */
  detonate: struct({ shooter: t.varuint }),
  /** A shot of the gunfire player `shooter`'s Wind-up gramophone plays at (x, y) (ADR-0221): gun `weapon`'s, nothing fired. */
  gramophone: struct({ shooter: t.varuint, seq: t.varuint, weapon: t.u8, x: pos, y: pos, angle: t.angle16 }),
  /** Player `shooter`'s gramophone `seq` shot to bits at (x, y) (ADR-0221 amended): its record silenced for good. */
  silenced: struct({ shooter: t.varuint, seq: t.varuint, x: pos, y: pos }),
  /**
   * Player `player`'s fishing line (ADR-0229): `what` (content `FishMoment`) befell it at (x, y); `tier` hooked (content
   * `FishTier`); its prize, `gun` a loot id + 1 or `item` a PickupKind + 1.
   */
  fish: struct({ player: t.varuint, what: t.u8, x: pos, y: pos, tier: t.u8, gun: t.varuint, item: t.u8 }),
  /** Player `shooter`'s C4 charge `seq` (ADR-0201) stuck at (x, y) `z` px up, on gardener `on` (0: none), or went dead. */
  charge: struct({ shooter: t.varuint, seq: t.varuint, x: pos, y: pos, z: pos, on: t.varuint, gone: t.bool }),
  /** How close `victim` came (the near miss): their killer `killer` had `hp` health of `max` and `shield` left; their team's. */
  margin: struct({ victim: t.varuint, killer: t.varuint, hp: t.varuint, max: t.varuint, shield: t.varuint }),
  /** A gardener off into the air or onto its way there (`up`), or down from it (ADR-0198): content FlightKind `kind` at (x, y). */
  air: struct({ player: t.varuint, kind: t.u8, up: t.bool, x: pos, y: pos }),
  /**
   * A battle royale's hot-air balloon `entity` (ADR-0199): `up`, its shadow from (x, y) to (tx, ty); `popped` by `by`, its
   * envelope's middle at (x, y), its basket falling to (tx, ty); `gone` over the sea at (x, y).
   */
  balloon: struct({
    entity: t.varuint,
    phase: t.enumOf(['up', 'popped', 'gone'] as const),
    by: t.varuint,
    x: pos,
    y: pos,
    tx: pos,
    ty: pos,
  }),
  /**
   * A battle royale's car `entity` (ADR-0214): driven into a wall by player `by` at `power` px/s (`crash`), or blown up at
   * (x, y), wrecked by player `by` (0: nobody's: `wreck`).
   */
  car: struct({
    entity: t.varuint,
    phase: t.enumOf(['crash', 'wreck'] as const),
    by: t.varuint,
    x: pos,
    y: pos,
    power: t.u8,
  }),
  /**
   * A battle royale's car `entity` heard at (x, y) (ADR-0214 amended): its radio on song `song` (content
   * `CAR.radio.songs`) `at` ticks into it, its engine at `engine` (0: nobody at its wheel; else 1 + its speed's share of
   * its top in 254ths). Sent to the squads within earshot (`CAR.radio.reach`).
   */
  radio: struct({ entity: t.varuint, x: pos, y: pos, song: t.u8, at: t.varuint, engine: t.u8 }),
  /**
   * The casino's roulette (ADR-0232): `entity` spun table `table` at (x, y), its ball to settle in pocket `pocket`
   * (`spin`); it settled there on red (`lost`) or on a gun, card `card` dropped in front of the table (`won`).
   */
  roulette: struct({
    phase: t.enumOf(['spin', 'won', 'lost'] as const),
    entity: t.varuint,
    table: t.u8,
    pocket: t.u8,
    card: t.varuint,
    x: pos,
    y: pos,
  }),
});
export type NetEvent = Infer<typeof NetEvent>;

/** Round state. Scores are rounds won. */
export const MatchInfo = struct({
  /** `lobby`: a room waiting to start; `ended`: the intermission between rounds (ADR-0015). */
  phase: t.enumOf(['playing', 'ended', 'lobby'] as const),
  winner: t.u8,
  endsIn: t.varuint,
  round: t.varuint,
  /** Rounds won, indexed by team. */
  score: array(t.u8),
  /** Teams knocked out of this round, in the order they fell (`MatchState.out`). */
  out: array(t.u8),
  /**
   * The range's boss dens (map.bossDens order, ADR-0100): `state` asleep / waking (in `wakesIn` ticks) / up; `kind` = the
   * BossKind that is up or wakes next.
   */
  dens: array(
    struct({
      state: t.enumOf(['asleep', 'waking', 'up'] as const),
      wakesIn: t.varuint,
      kind: t.u8,
    }),
  ),
  /** Players waiting to respawn (ADR-0016) — public, like a scoreboard: NetId, team, ticks left. */
  down: array(struct({ id: t.varuint, team: t.u8, respawnIn: t.varuint })),
  /** Ticks this round has been played (ADR-0018; stops when it is over): the HUD's round clock. */
  clock: t.varuint,
  /**
   * The Zombie Nights run (ADR-0098): day `day` (`endsIn` ticks left of it) or the night after it; at night the zombies
   * standing (`alive`), still to rise (`left`) and in all (`total`); the nights `survived`; the coming (or current) night's
   * `gates` (map.gates indices). `king` (content KingStage, ADR-0164): where the Buried King's fight is, `endsIn` then the
   * ticks until the party is in the Barrow (the ground giving way) or back at the Greenhouse (his end). `from`: the night
   * the run started on (1; a later one in the menus' bot game). `waiting`: the newcomers day 1 waits for, till
   * each has built a Sprinkler (NetIds, ADR-0168 amended: its clock holds meanwhile). `speed`: ticks a `TICK_MS` the run
   * plays at (1, or 2 at double speed: ADR-0173; the client's clock and stepper follow it). `size`: the run's party size
   * so far (ADR-0176: the most gardeners a night was scaled for, or who are in it now): which of its map's boards it is on.
   * `chomper`: how many ticks into its drive Chomper is (ADR-0180, content `chomperAt`; 0 before it, its drive's length
   * after). `waits`: the day waits for everyone's READY, its clock held (ADR-0183: someone here turned Auto-start nights
   * off). `craters`: the cells tonight's meteors came down on (ADR-0186), holes the dead climb out of till dawn. The deck
   * the run was dealt (ADR-0226, content `deckSent`): its `week` (0: none), or a dev knob's `variants`, dealt every night.
   */
  zombies: optional(
    struct({
      speed: t.u8,
      day: t.varuint,
      phase: t.enumOf(['day', 'night'] as const),
      king: t.u8,
      endsIn: t.varuint,
      alive: t.varuint,
      left: t.varuint,
      total: t.varuint,
      survived: t.varuint,
      gates: array(t.u8),
      from: t.varuint,
      waiting: array(t.varuint),
      waits: t.bool,
      size: t.u8,
      chomper: t.varuint,
      craters: array(t.varuint),
      week: t.varuint,
      variants: array(t.u8),
    }),
  ),
  /**
   * A battle royale's round (ADR-0120): the Frost's circle now (`x`, `y`, radius `r`) and the one it closes to (`nx`,
   * `ny`, `nr`); its phase (content `ROYALE.zone` index, its length once every circle closed), whether it is closing in
   * (`shrinking`) and the ticks until that changes (`endsIn`); the gardeners (`alive`) and teams (`teams`) still standing;
   * by `map.stairs`, the round's `clock` the Frost began pouring down each stairwell + 1, 0 while it hasn't (`flood`,
   * ADR-0196 amended: sim `floodCell` says where it lies along the tunnels).
   */
  royale: optional(
    struct({
      x: pos,
      y: pos,
      r: pos,
      nx: pos,
      ny: pos,
      nr: pos,
      phase: t.u8,
      shrinking: t.bool,
      endsIn: t.varuint,
      alive: t.u8,
      teams: t.u8,
      flood: array(t.varuint),
      /**
       * Its stone altars (ADR-0189, amended by ADR-0202; by `map.altars`): each one's phase (sim `AltarPhase`), its runes'
       * charge (ticks, of `ROYALE.altar.wakeTicks`), the ticks until its guardian rises (0: risen) and until the fight is
       * lost or its rest is over, its guardian (a NetId; 0: none up), and who woke it (a NetId).
       */
      altars: array(
        struct({
          phase: t.u8,
          charge: t.u8,
          rise: t.varuint,
          endsIn: t.varuint,
          boss: t.varuint,
          by: t.varuint,
        }),
      ),
      /** Its wanted boards' posters (ADR-0229; by `map.posters`): each one's sim `PosterPhase` (old, lit, taken). */
      posters: array(t.u8),
      /**
       * Who has a bounty on them now (ADR-0229), for everyone whatever they see: a NetId, its squad, where they stand, the
       * ticks left of it.
       */
      wanted: array(struct({ id: t.varuint, team: t.u8, x: pos, y: pos, left: t.varuint })),
    }),
  ),
});
export type MatchInfo = Infer<typeof MatchInfo>;

/** One tick's events (ADR-0204: a snapshot carries those of every tick since the one sent before it). */
export const TickEvents = struct({ tick: t.varuint, events: array(NetEvent) });
export type TickEvents = Infer<typeof TickEvents>;

/**
 * A snapshot's body (ADR-0204): the round; its entities, whole (`base` 0) or written against snapshot `base` (a tick the
 * client said it holds, `inputs.seen`): those new since then whole (`entities`), the fields that changed (`changed`),
 * the ids gone (`gone`); and its events by the tick they happened in, oldest first. The round and the events are encoded
 * once per team, the entities once per team and base.
 */
export const SnapshotBody = struct({
  match: MatchInfo,
  base: t.varuint,
  entities: array(EntityState),
  changed: array(EntityChange),
  gone: array(t.varuint),
  events: array(TickEvents),
});
export type SnapshotBody = Infer<typeof SnapshotBody>;
/** A body's entities as written: whole, or against a base. */
export type SnapshotEntities = Pick<SnapshotBody, 'base' | 'entities' | 'changed' | 'gone'>;

export const SnapshotHeader = struct({
  tick: t.varuint,
  /** Last input seq the server simulated for this client (0 = none yet). */
  ack: t.varuint,
  /** Inputs waiting in the server queue for this client; negative = the server had to guess. */
  inputBuffer: t.i8,
  self: optional(SelfState),
});
export type SnapshotHeader = Infer<typeof SnapshotHeader>;

/** A snapshot as it comes off the wire: its entities perhaps written against a base (`applyEntities` makes them whole). */
export type WireSnapshot = SnapshotHeader & SnapshotBody;
/** A snapshot as a client holds it: every entity whole. */
export type Snapshot = SnapshotHeader & Pick<SnapshotBody, 'match' | 'events'> & { entities: EntityState[] };

const Welcome = struct({
  version: t.u16,
  playerId: t.varuint,
  team: t.u8,
  tickRate: t.u8,
  tick: t.varuint,
  mapId: t.string,
  /** Cells Railgun tunnels burnt out of it so far (ADR-0038); later ones come as `carve` events. */
  carved: array(t.varuint),
  /** Things of the map broken so far this round (ADR-0188); later ones come as `thingBroke` events. */
  smashed: array(t.varuint),
  /**
   * How much your shots are helped (content ASSIST_LEVELS, same order; ADR-0026, ADR-0054): the client's visual shots
   * bend the same way.
   */
  assist: t.enumOf(['standard', 'extra'] as const),
  /** You took back the seat you dropped out of (ADR-0045): same player, team, gold; a fresh session on this side. */
  rejoin: t.bool,
});
const Pong = struct({ time: t.f64, tick: t.varuint });
/** Pinned like `hello` (ServerType 4, one string): every client from any version must read a refusal. */
const Reject = struct({ reason: t.string });
/**
 * Who is in the match (names for the HUD, the room hub's seats, ready markers). Sent to everyone whenever someone
 * joins, leaves, changes team or seat, or their ready state. Bots are always ready. `teamSize`: the line-up's players a
 * team (0: no line-up, any number a team, as in the dev lobby and offline); `open`: a custom room's `Friends can join` (its owner flips it on the lobby socket, the
 * server tells the match; false in any other room); `viewers`: a creator's room's `Viewers can join` (ADR-0128: its
 * `/c/<login>` link drops viewers in; flipped like `open`); `creator`: the room's creator's Twitch login, the link every
 * invite from inside it hands out ('' for any other room); `game`: what a games room plays (ADR-0174: its owner picks
 * it on its stage, on the lobby socket; none in any other room); `seat`: the seat on its team (0 … teamSize − 1, unique on the
 * team; the hub's stage shows seats 0-2 in front, 3-4 behind; the round starts it on its team's spawn of that index);
 * `hat`: content HAT_KEYS index (bots: 0); `gun`: the skin on their guns, content GUN_SKIN_KEYS index (ADR-0095;
 * bots: 0, stock); `look`: content `packLook` (ADR-0073; bots: `lookOf` their name hashed, a guest without an account:
 * their id); `host`: the room's owner; `away`: dropped out, their seat held for a rejoin
 * (ADR-0045); `account`: the player's account id ('' for a bot or a guest without one): a tap on them on a stage opens
 * their profile or adds them as a friend; `creator`: a verified creator's account (ADR-0128: the purple check by their
 * name); `top`: their place on the Creator Program's top creators' board as they joined (ADR-0138 amended: 1 to content
 * `TOP_MARKED`, a flame by their name, the first a crown; 0: none); `kills` / `deaths` / `assists`: the round's so far
 * (the Tab scoreboard, ADR-0084; sent again after every kill); `unaided`: their shots get no aim help (a bot's, ADR-0026,
 * and one passing for a player, whose `bot` is false, ADR-0147): clients draw them flying as aimed, as the server does;
 * `tally`: the players their account ever killed, its stored rounds' as it joined and this match's since (a polished
 * gun's counter, ADR-0148; 0: a bot or a guest); `fast`: they want Zombie Nights at double speed (ADR-0173; bots never
 * ask).
 */
const Roster = struct({
  teamSize: t.u8,
  open: t.bool,
  viewers: t.bool,
  creator: t.string,
  game: optional(t.enumOf(ROOM_GAMES)),
  players: array(
    struct({
      id: t.varuint,
      team: t.u8,
      seat: t.u8,
      name: t.string,
      ready: t.bool,
      fast: t.bool,
      bot: t.bool,
      unaided: t.bool,
      host: t.bool,
      away: t.bool,
      hat: t.u8,
      gun: t.u8,
      look: t.varuint,
      account: t.string,
      creator: t.bool,
      top: t.u8,
      kills: t.varuint,
      deaths: t.varuint,
      assists: t.varuint,
      /** HP dealt to other gardeners this round, their shields' too (a battle royale's end card, ADR-0219). */
      damage: t.varuint,
      tally: t.varuint,
    }),
  ),
});
export type RosterEntry = Infer<typeof Roster>['players'][number];
/**
 * You finished one of your daily challenges this round (ADR-0123; only your connection hears it): its content
 * `CHALLENGES` key, whether it won the day (the day's third, or a catch-up's last) and the season tier that won (0 for
 * none, or a day won between seasons: it paid `DAILY_SEEDS`).
 */
const Challenge = struct({ key: t.string, won: t.bool, tier: t.u8 });
/**
 * The game was updated (a deploy, ADR-0125): this room's server drains, so the round in play is the room's last (none
 * follows an intermission either). Sent to everyone in the room when the drain begins, and to anyone who joins after.
 */
const Update = struct({});
/**
 * Who spectates you (ADR-0124 amended, feedback #25; only your connection hears it): every friend or party mate watching
 * your game now, by the name the match knows them by (a stand-in for one who hides theirs), their account (streamer mode's
 * stand-ins) and whether they are a verified creator. Sent when it changes, and as you join or rejoin while anyone does.
 */
const Watchers = struct({
  watchers: array(struct({ name: t.string, account: t.string, creator: t.bool })),
});
/**
 * A line of the room's chat (ADR-0170): who said it (their NetId: the roster names them) and what, rude words starred; to
 * their `team` only, or whispered `to` one player (their NetId; 0: no whisper; the speaker hears their own as said to them).
 */
const Chat = struct({ from: t.varuint, text: t.string, team: t.bool, to: t.varuint });
/** The gateway's answer to the client's `rtc` offer (ADR-0178); '': no UDP lane here (the socket carries everything). */
const Rtc = struct({ sdp: t.string });
/**
 * A snapshot's events (ADR-0178), on the socket while the snapshot goes on the UDP lane, which may lose it: the client
 * plays a tick's events once, from whichever comes first. By tick, as the snapshot has them (ADR-0204).
 */
const Events = struct({ events: array(TickEvents) });

export type ServerMessage =
  | ({ type: 'welcome' } & Infer<typeof Welcome>)
  | ({ type: 'snapshot' } & WireSnapshot)
  | ({ type: 'pong' } & Infer<typeof Pong>)
  | ({ type: 'reject' } & Infer<typeof Reject>)
  | ({ type: 'roster' } & Infer<typeof Roster>)
  | ({ type: 'challenge' } & Infer<typeof Challenge>)
  | ({ type: 'update' } & Infer<typeof Update>)
  | ({ type: 'watchers' } & Infer<typeof Watchers>)
  | ({ type: 'chat' } & Infer<typeof Chat>)
  | ({ type: 'rtc' } & Infer<typeof Rtc>)
  | ({ type: 'events' } & Infer<typeof Events>);

const ServerType = {
  welcome: 1,
  snapshot: 2,
  pong: 3,
  reject: 4,
  roster: 5,
  challenge: 6,
  update: 7,
  watchers: 8,
  chat: 9,
  rtc: 10,
  events: 11,
} as const;

/** A server message's type from its first byte alone, or null: a gateway routes on it without decoding the rest. */
export function serverMessageType(bytes: Uint8Array): ServerMessage['type'] | null {
  for (const [type, b] of Object.entries(ServerType)) if (b === bytes[0]) return type as ServerMessage['type'];
  return null;
}

// ─── Encoding ───────────────────────────────────────────────────────────────────────────────────────

const writer = new ByteWriter(1024);

export function encodeClientMessage(msg: ClientMessage): Uint8Array {
  ClientMessage.write(writer.reset(), msg);
  return writer.finish();
}

export function decodeClientMessage(bytes: Uint8Array): ClientMessage {
  return ClientMessage.read(new ByteReader(bytes));
}

/** The protocol version in a `hello` from any version (variant 0, a u16 first), or null for any other message. */
export function helloVersion(bytes: Uint8Array): number | null {
  return bytes.length >= 3 && bytes[0] === 0 ? new ByteReader(bytes.subarray(1)).u16() : null;
}

export function encodeSnapshotBody(body: SnapshotBody): Uint8Array {
  SnapshotBody.write(writer.reset(), body);
  return writer.finish();
}

const BODY = SnapshotBody.shape;

/** A body's round, encoded on its own (ADR-0204): the first of its three parts, the same for every team. */
export function encodeMatchInfo(match: MatchInfo): Uint8Array {
  BODY.match.write(writer.reset(), match);
  return writer.finish();
}

/** A body's entities, whole or against a base, encoded on their own (ADR-0204): its second part, per team and base. */
export function encodeSnapshotEntities(e: SnapshotEntities): Uint8Array {
  const w = writer.reset();
  BODY.base.write(w, e.base);
  BODY.entities.write(w, e.entities);
  BODY.changed.write(w, e.changed);
  BODY.gone.write(w, e.gone);
  return w.finish();
}

/** A body's events by tick, encoded on their own (ADR-0204): its last part, per team. */
export function encodeTickEvents(events: TickEvents[]): Uint8Array {
  BODY.events.write(writer.reset(), events);
  return writer.finish();
}

/**
 * Header is per-recipient; the body comes from `encodeSnapshotBody`, or as its parts in order (`encodeMatchInfo`,
 * `encodeSnapshotEntities`, `encodeTickEvents`), each shared by every recipient it is the same for.
 */
export function encodeSnapshot(header: SnapshotHeader, ...body: Uint8Array[]): Uint8Array {
  const w = writer.reset();
  w.u8(ServerType.snapshot);
  SnapshotHeader.write(w, header);
  for (const part of body) w.bytes(part);
  return w.finish();
}

export function encodeServerMessage(msg: Exclude<ServerMessage, { type: 'snapshot' }>): Uint8Array {
  const w = writer.reset();
  switch (msg.type) {
    case 'welcome':
      w.u8(ServerType.welcome);
      Welcome.write(w, msg);
      break;
    case 'pong':
      w.u8(ServerType.pong);
      Pong.write(w, msg);
      break;
    case 'reject':
      w.u8(ServerType.reject);
      Reject.write(w, msg);
      break;
    case 'roster':
      w.u8(ServerType.roster);
      Roster.write(w, msg);
      break;
    case 'challenge':
      w.u8(ServerType.challenge);
      Challenge.write(w, msg);
      break;
    case 'update':
      w.u8(ServerType.update);
      Update.write(w, msg);
      break;
    case 'watchers':
      w.u8(ServerType.watchers);
      Watchers.write(w, msg);
      break;
    case 'chat':
      w.u8(ServerType.chat);
      Chat.write(w, msg);
      break;
    case 'rtc':
      w.u8(ServerType.rtc);
      Rtc.write(w, msg);
      break;
    case 'events':
      w.u8(ServerType.events);
      Events.write(w, msg);
      break;
  }
  return w.finish();
}

export function decodeServerMessage(bytes: Uint8Array): ServerMessage {
  const r = new ByteReader(bytes);
  const type = r.u8();
  switch (type) {
    case ServerType.welcome:
      return { type: 'welcome', ...Welcome.read(r) };
    case ServerType.snapshot:
      return { type: 'snapshot', ...SnapshotHeader.read(r), ...SnapshotBody.read(r) };
    case ServerType.pong:
      return { type: 'pong', ...Pong.read(r) };
    case ServerType.reject:
      return { type: 'reject', ...Reject.read(r) };
    case ServerType.roster:
      return { type: 'roster', ...Roster.read(r) };
    case ServerType.challenge:
      return { type: 'challenge', ...Challenge.read(r) };
    case ServerType.update:
      return { type: 'update', ...Update.read(r) };
    case ServerType.watchers:
      return { type: 'watchers', ...Watchers.read(r) };
    case ServerType.chat:
      return { type: 'chat', ...Chat.read(r) };
    case ServerType.rtc:
      return { type: 'rtc', ...Rtc.read(r) };
    case ServerType.events:
      return { type: 'events', ...Events.read(r) };
    default:
      throw new Error(`Unknown server message type ${type}`);
  }
}
